import * as Sentry from '@sentry/vue'
import { useApolloClient } from '@vue/apollo-composable'
import { ref } from 'vue'

import { FilterType } from '@/modules/contentCreator/creator/enums/FilterType'
import GET_SCHEDULED_CONTENTS from '@/modules/contentCreator/creator/queries/getScheduledContents.gql'
import { buildContentsTimelineInput } from '@/modules/contentCreator/creator/scheduledContents'

const PER_PAGE = 100
const MAX_PAGES = 20

// Same query as the Scheduled Posts timeline, walked until every page is loaded.
export function useScheduledContents() {
  const { client } = useApolloClient()
  const scheduledContents = ref([])
  const loading = ref(false)
  // The last load failure, cleared by the next successful load. Lets callers tell
  // "no scheduled posts" apart from "they could not be loaded".
  const error = ref<unknown>(null)

  async function refetch() {
    loading.value = true
    try {
      const all = []
      let page = 1
      let total = Infinity
      while (all.length < total && page <= MAX_PAGES) {
        const { data } = await client.query({
          query: GET_SCHEDULED_CONTENTS,
          variables: {
            input: buildContentsTimelineInput({
              timeRange: FilterType.ALL,
              page,
              perPage: PER_PAGE,
            }),
          },
          fetchPolicy: 'network-only',
        })
        const payload = data?.scheduledContents
        const batch = payload?.scheduledContents ?? []
        total = payload?.total ?? all.length
        all.push(...batch)
        if (!batch.length) break
        page += 1
      }
      scheduledContents.value = all
      error.value = null
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  // Callers await refetch() themselves later; the first load has no caller to catch it.
  refetch().catch((error) => {
    Sentry.captureException(error, {
      tags: { feature: 'scheduled-contents', operation: 'initial-load' },
    })
  })
  return { scheduledContents, loading, error, refetch }
}
